











































































































import {
    defineComponent,
    useContext,
    ref,
    onMounted,
    computed,
    useRoute,
} from "@nuxtjs/composition-api";
import { SfOverlay } from "@storefront-ui/vue";
import HeaderLogo from "components/theme/Header/Logo.vue";
import { useUiState, useConfig } from "@wemade-vsf/composables";
import { useSearch } from "composables/useSearch";
import { useUser } from "@wemade-vsf/customer";
import MegaMenu from "components/megamenu/MegaMenu.vue";
import CartNavigation from "components/theme/Header/CartNavigation.vue";
import MobileNavigationBar from "components/theme/Header/MobileNavigationBar.vue";
import TopNavigation from "components/theme/Header/TopNavigation.vue";
import { SfButton, SfToggleSwitch } from "@storefront-ui/vue";

const headerRef = ref<HTMLElement | null>(null);

export default defineComponent({
    name: "AppHeader",
    components: {
        CartNavigation,
        HeaderLogo,
        MegaMenu,
        MobileNavigationBar,
        SfOverlay,
        SfButton,
        SfToggleSwitch,
        TopNavigation,
        SearchBar: () => import("components/search/SearchBar.vue"),
        SearchResults: () =>
            import(
                /* webpackPrefetch: true */ "components/search/SearchResults.vue"
            ),
        MobileSidebar: () => import("components/megamenu/MegaMenuSidebar.vue"),
        TopBar: () => import("components/theme/Header/TopBar.vue"),
        AccountIcon: () => import("components/theme/Icons/AccountIcon.vue"),
        PhoneIcon: () => import("components/theme/Icons/PhoneIcon.vue"),
    },
    props: {
        menuOrientation: {
            type: String,
            default: "horizontal",
        },
        useSticky: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showTopBar: true,
        };
    },
    methods: {
        hideTopBar() {
            this.showTopBar = false;
        },
        isScrolling() {
            requestAnimationFrame(() => {
                const scrollingClass = "body-scrolling";
                const header = this.$refs.layoutRef as HTMLElement;

                if (!header) {
                    return;
                }
                if (
                    window.scrollY > 0 &&
                    header.classList.contains(scrollingClass)
                ) {
                    return;
                }

                if (window.scrollY > 0) {
                    header.classList.add(scrollingClass);
                } else {
                    header.classList.remove(scrollingClass);
                }
            });
        },
    },
    mounted() {

        let resizeObserver: ResizeObserver | null = null;

        if (process.browser && "ResizeObserver" in window) {
            resizeObserver = new ResizeObserver(function (entries) {
                let rect = entries[0].contentRect;

                document.documentElement.style.setProperty(
                    "--site-width",
                    rect.width + ""
                );
                document.documentElement.style.setProperty(
                    "--site-header",
                    rect.height + "px"
                );
            });
            resizeObserver.observe(this.$refs.layoutRef as HTMLElement);
        }

        window.addEventListener("scroll", this.isScrolling);
    },
    setup() {
        const {
            app: { $wm },
            $config,
        } = useContext();
        const { isMobileMenuOpen, toggleMobileMenu, hideSearch } = useUiState();

        const { isSearchOpen, closeSearch } = useSearch();

        const { isAuthenticated, load: loadUser, user } = useUser();
        const { config } = useConfig();

        const showWishlist = $config.wishlist?.enable ?? true;
        const showCart = computed(() => {
            if (!user.value) {
                return !(config.value?.storefront?.hide_cart_button ?? false);
            }
            return !user.value.hide_cart_button;
        });

        const topNavigationPosition =
            $wm?.theme?.topNavigationPosition ?? "menu";

        const topBarConfig = computed(() => {
            if (
                config.value?.storefront?.top_bar_block_enabled &&
                config.value?.storefront?.top_bar_block_identifier
            ) {
                return {
                    background:
                        config.value.storefront.top_bar_block_background_color,
                    identifier:
                        config.value.storefront.top_bar_block_identifier,
                };
            }
            return null;
        });

        const sticky = config.value?.storefront?.sticky_header ?? false;

        const route = useRoute();

        const isThankYou = computed(() =>
            route.value.name.startsWith("checkout-thank-you")
        );

        onMounted(async () => {
            if (isAuthenticated.value && !user.value) {
                await loadUser();
            }
        });

        return {
            isMobileMenuOpen,
            isSearchOpen,
            closeSearch,
            sticky,
            showWishlist,
            showCart,
            topBarConfig,
            topNavigationPosition,
            toggleMobileMenu,
            hideSearch,
            headerRef,
            isThankYou,
        };
    },
});
